import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { receiveMessage, refreshChatMessages } from '../Reducers/actions/chatActions';
import useAdaptivePolling from './useAdaptivePolling';

const useRealTimeChat = (chatId = null) => {
  const dispatch = useDispatch();
  const currentChat = useSelector((state) => state.chat.currentChat);
  const lastMessageCountRef = useRef(0);
  const pollingRef = useRef(null);

  const pollForMessages = async () => {
    if (!chatId) {
      throw new Error('No chatId provided for polling');
    }
    
    // Poll for messages in a specific chat
    const result = await dispatch(refreshChatMessages(chatId));
    
    // Check if the action was successful based on the result structure
    if (!result.success) {
      throw new Error(`API request failed: ${result.response?.message || 'Unknown error'}`);
    }
    
    // Check if we received new messages (not from our own sending)
    const currentMessageCount = currentChat?.messages?.length || 0;
    if (currentMessageCount > lastMessageCountRef.current) {
      // Trigger fast polling when we receive new messages
      if (pollingRef.current?.triggerFastPoll) {
        pollingRef.current.triggerFastPoll();
      }
    }
    
    // Update the message count reference
    lastMessageCountRef.current = currentMessageCount;
  };

  // Initialize message count reference
  if (currentChat?.messages && lastMessageCountRef.current === 0) {
    lastMessageCountRef.current = currentChat.messages.length;
  }

  // Set up adaptive polling for chat page only, when chatId is available
  const adaptivePolling = useAdaptivePolling({
    pollingFunction: chatId ? pollForMessages : null,
    enabledRoutes: ['/chat'],
    context: `chat-${chatId || 'none'}`
  });

  // Store polling reference for triggering fast polling from within pollForMessages
  pollingRef.current = adaptivePolling;

  // Return utility functions for manual operations
  return {
    ...adaptivePolling,
    pollNow: adaptivePolling.pollNow,
    stopPolling: adaptivePolling.stopPolling,
    triggerFastPoll: adaptivePolling.triggerFastPoll // Expose for message sending
  };
};

export default useRealTimeChat;